/**
 * Die lokale Datenbank des Geraets -- IndexedDB ueber Dexie (CLAUDE.md §2).
 *
 * KONZEPT.md §15.1: „Jedes Geraet haelt eine **vollstaendige lokale Kopie**
 * von Artikeln, Lagerorten, Einsatzstellen und Benutzern." Dazu kommt das
 * ganze Buchungslog: Daraus rechnet das Geraet den Zustand selbst, mit
 * derselben reinen Funktion wie der Server (ADR 0010).
 *
 * Wer schreibt hier?
 *
 * - `buchungen` (die Warteschlange) nur `api/speicher.ts`.
 * - Alles andere nur der Abgleich (`abgleich.ts`). Die Oberflaeche liest --
 *   was sie am Server geaendert hat, reicht sie ueber `artikelUebernehmen` und
 *   `packstueckUebernehmen` ein.
 *
 * Die Tabelle `buchungen` heisst seit Version 1 so und haelt die
 * **Warteschlange**, nicht das Log. Sie umzubenennen hiesse, auf jedem Geraet
 * die noch nicht uebertragenen Buchungen umzuziehen -- genau die Daten, die es
 * nirgends sonst gibt.
 */

import { Dexie, type Table } from "dexie";
import type {
  Artikel,
  BenutzerKurz,
  Buchung,
  BuchungAnlegen,
  Einsatzstelle,
  Lagerort,
  StatusArt,
  Zustand,
} from "@thw/contracts";

/** Eine Buchung in der Warteschlange, mit dem was ihr Schicksal beschreibt. */
export interface WartendeBuchung {
  id: string;
  buchung: BuchungAnlegen;
  /** Wann sie erfasst wurde -- damit die Anzeige „seit wann" sagen kann. */
  angelegt_am: string;
  versuche: number;
  /** Letzter Fehlertext, falls der Server sie abgewiesen hat. */
  grund: string | null;
  zustand: "wartet" | "abgelehnt";
}

/** Der abgeleitete Zustand eines Artikels, wie ihn das Log ergibt. */
export type GespeicherterZustand = Zustand & { artikel_id: string };

/**
 * Eine eigene Buchung, die beim Abgleich ueberstimmt war (KONZEPT.md §15.4).
 *
 * `gelesen` ist eine Zahl und kein Wahrheitswert: IndexedDB kann nach
 * `true`/`false` nicht suchen.
 */
export interface SyncHinweis {
  /** Kennung der ueberstimmten Buchung. */
  id: string;
  artikel_id: string;
  buchung: Buchung;
  /** Die Buchung, die stattdessen galt, als der Abgleich es bemerkte. */
  gewinner: Buchung;
  erkannt_am: string;
  gelesen: 0 | 1;
}

/** Eigene, uebertragene Buchung, die noch auf Ueberstimmung zu pruefen ist. */
export interface OffenePruefung {
  id: string;
  /** Ab diesem Stand des Logs laesst sich pruefen. */
  folge: number;
}

/**
 * Ein unbekannter Code, gescannt ohne Netz (ADR 0026). Liegt nur auf diesem
 * Geraet und wartet, bis jemand mit Netz das Geraet anlegt.
 */
export interface NachzuerfassenderCode {
  /** Der Code, wie er gelesen wurde -- zugleich der Schluessel: nie doppelt. */
  code: string;
  /** Die Schreibweise einer Inventarnummer, falls der Code danach aussieht. */
  vorschlag: string | null;
  erfasst_am: string;
  /** Der Behaelter, in dem man gerade war -- spaeter als Ort vorbelegt. */
  ort_id: string | null;
}

export interface MetaEintrag {
  schluessel: string;
  wert: unknown;
}

class Datenbank extends Dexie {
  buchungen!: Table<WartendeBuchung, string>;
  artikel!: Table<Artikel, string>;
  zustaende!: Table<GespeicherterZustand, string>;
  lagerorte!: Table<Lagerort, string>;
  einsatzstellen!: Table<Einsatzstelle, string>;
  benutzer!: Table<BenutzerKurz, string>;
  statusArten!: Table<StatusArt, string>;
  log!: Table<Buchung, string>;
  pruefen!: Table<OffenePruefung, string>;
  hinweise!: Table<SyncHinweis, string>;
  meta!: Table<MetaEintrag, string>;
  nachzuerfassen!: Table<NachzuerfassenderCode, string>;

  constructor() {
    super("thw-wawi");
    // Primaerschluessel zuerst, danach die Indizes.
    this.version(1).stores({
      buchungen: "id, zustand, angelegt_am",
    });
    // Stufe 3: die lokale Kopie. Nur Tabellen dazu -- die Warteschlange
    // bleibt, wie sie ist.
    this.version(2).stores({
      buchungen: "id, zustand, angelegt_am",
      artikel: "id, inventarnummer",
      zustaende: "artikel_id, lagerort_id",
      lagerorte: "id, kennung, eltern_id",
      einsatzstellen: "id",
      benutzer: "id",
      statusArten: "schluessel",
      log: "id, artikel_id, folge",
      pruefen: "id",
      hinweise: "id, artikel_id, gelesen",
      meta: "schluessel",
    });
    // ADR 0026: unbekannte Codes, die ohne Netz gescannt wurden. Nur dazu.
    this.version(3).stores({
      buchungen: "id, zustand, angelegt_am",
      artikel: "id, inventarnummer",
      zustaende: "artikel_id, lagerort_id",
      lagerorte: "id, kennung, eltern_id",
      einsatzstellen: "id",
      benutzer: "id",
      statusArten: "schluessel",
      log: "id, artikel_id, folge",
      pruefen: "id",
      hinweise: "id, artikel_id, gelesen",
      meta: "schluessel",
      nachzuerfassen: "code, erfasst_am",
    });
  }
}

export const db = new Datenbank();

/** Die Schluessel der Tabelle `meta` -- an einer Stelle, damit sich keiner vertippt. */
export interface Meta {
  /** Bis zu dieser Folgenummer liegt das Log vollstaendig vor. */
  folge_stand: number;
  /** Wann der Stammstand zuletzt geholt wurde (Uhr des Geraets). */
  stammstand_am: string;
  /** Server minus Geraet in Millisekunden, beim letzten Stammabruf gemessen. */
  uhr_abweichung_ms: number;
  basis_adresse: string;
  /** Letzter vollstaendig gelungener Abgleich (Uhr des Geraets). */
  letzter_abgleich: string;
}

export async function metaLesen<K extends keyof Meta>(schluessel: K): Promise<Meta[K] | null> {
  const eintrag = await db.meta.get(schluessel);
  return eintrag === undefined ? null : (eintrag.wert as Meta[K]);
}

export async function metaSchreiben<K extends keyof Meta>(
  schluessel: K,
  wert: Meta[K],
): Promise<void> {
  await db.meta.put({ schluessel, wert });
}
